import { useMemo, useState } from 'react'
import { useCurrentUnit } from '../hooks/useCurrentUnit.js'
import { useHuddleData } from '../hooks/useHuddleData.js'
import AiReceiptsCard from './AiReceiptsCard.jsx'
import { useMarketingDrafts } from '../hooks/useMarketingDrafts.js'
import { useOnboardingStatus } from '../hooks/useOnboardingStatus.js'
import { formatMoney, formatQty } from '../lib/format.js'
import { AGENT_LABELS } from '../lib/agents.js'
import AskAiBar from './AskAiBar.jsx'
import FirstWeekMilestonesCard from './FirstWeekMilestonesCard.jsx'
import HuddleBriefCard from './HuddleBriefCard.jsx'
import MarketingNudgeCard, { shouldShowMarketingNudge } from './MarketingNudgeCard.jsx'
import OnboardingWizard from './OnboardingWizard.jsx'
import RosterAssignmentModal from './RosterAssignmentModal.jsx'
import { Skeleton, SkeletonCard, SkeletonList } from './Skeleton.jsx'
import StatusPill from './StatusPill.jsx'
import TimeSavedCard from './TimeSavedCard.jsx'
import { toneFor, labelFor } from '../lib/pillStatus.js'
import Toast from './Toast.jsx'

// Daily Huddle screen (SOP 4) — the manager's 30-second morning view.
// All data is read-only roll-ups of what the other screens already
// track. Each metric card is tappable and switches to the relevant
// tab so the manager can drill in for action.
//
// The summary sentence at the top is generated by a small heuristic
// (see generateSummary below). It picks the most actionable signal
// in the current data — over-target waste, critical inventory,
// big day-over-day swings, or "quiet day" as the default.
export default function DailyHuddleScreen({ onSwitchTab }) {
  // Active unit — scopes the AI Receipts card to the unit the manager is
  // viewing. Hook called BEFORE any early return (React #310).
  const { currentRestaurantId } = useCurrentUnit()

  const {
    settings,
    todayWasteTotal,
    yesterdayTotal,
    savingsSummary,
    itemsNeedingReorder,
    criticalItems,
    restockCost,
    todayReceiveTotal,
    todayReceiveCount,
    todaySupplierCount,
    topWastedToday,
    todayAssignments,
    activeStaff,
    todayLabor,
    todaySales,
    wastePctOfSales,
    laborPctOfSales,
    receivingsPctOfSales,
    primeCost7d,
    itemCount,
    createAssignment,
    deleteAssignment,
    loading,
  } = useHuddleData()

  // Marketing draft inbox count — surfaced as a banner so the
  // manager sees "5 drafts ready to review" the morning the cron
  // dropped them off, without having to remember to check the
  // Marketing tab.
  const marketing = useMarketingDrafts()
  const draftCount = marketing.counts?.draft || 0

  // Roster modal + per-row remove state for "Today's Roster" section.
  const [rosterModalOpen, setRosterModalOpen]         = useState(false)
  const [removingAssignmentId, setRemovingAssignmentId] = useState(null)
  const [toast, setToast]                             = useState('')

  // Onboarding state — drives the welcome card + wizard for new managers.
  const {
    restaurant: onboardingRestaurant,
    loading: onboardingLoading,
    markOnboarded,
    updateRestaurantBasics,
  } = useOnboardingStatus()
  const [wizardOpen, setWizardOpen] = useState(false)
  const showWelcome = !onboardingLoading
    && onboardingRestaurant
    && !onboardingRestaurant.onboarded
    && !wizardOpen

  async function handleDismissWelcome() {
    try {
      await markOnboarded()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleDismissWelcome]', e)
      setToast('Could not save. Try again.')
    }
  }

  const dailyTarget = Number(settings?.daily_target ?? 50)

  // Today's date in YYYY-MM-DD (browser-local, en-CA gives ISO format).
  // Passed to the roster modal so it inserts shifts on the right day.
  const todayDateStr = new Date().toLocaleDateString('en-CA')

  async function handleAddToRoster(payload) {
    await createAssignment(payload)
    setToast('Added to today\u2019s roster')
  }

  async function handleRemoveAssignment(a) {
    if (removingAssignmentId) return
    setRemovingAssignmentId(a.id)
    try {
      await deleteAssignment(a.id)
      setToast('Removed from roster')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[DailyHuddle] remove assignment failed', e)
      setToast('Could not remove. Try again.')
    } finally {
      setRemovingAssignmentId(null)
    }
  }

  // Summary sentence — heuristic prioritizes the most actionable signal.
  // NOTE: this useMemo MUST run on every render (React Rules of Hooks).
  // The skeleton early-return below comes AFTER all hook calls.
  const summary = useMemo(
    () =>
      generateSummary({
        todayWasteTotal,
        yesterdayTotal,
        dailyTarget,
        criticalItems,
        itemsNeedingReorder,
        savingsSummary,
        topWastedToday,
      }),
    [
      todayWasteTotal, yesterdayTotal, dailyTarget,
      criticalItems, itemsNeedingReorder, savingsSummary, topWastedToday,
    ]
  )

  // First-paint loading state: replace the real layout with skeleton
  // placeholders so the screen never shows zeroed-out real values
  // (e.g. "$0 saved · 0 items below par") that flicker into the
  // correct numbers as Supabase responses land. Placed AFTER all
  // hooks have been called so we don't violate Rules of Hooks.
  if (loading && Number(savingsSummary?.event_count ?? 0) === 0
      && itemsNeedingReorder.length === 0) {
    return (
      <div className="min-h-full bg-white pb-12">
        <main className="mx-auto max-w-screen-sm">
          <div className="px-4 pt-4">
            <Skeleton className="h-3 w-40" />
          </div>
          {/* Summary card placeholder */}
          <div className="mx-4 mt-2 rounded-2xl border-l-4 border-amber-brand-200 bg-amber-brand-50/40 px-4 py-3">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-2 h-4 w-3/4" />
          </div>
          {/* 2×2 metric grid placeholder */}
          <div className="mx-4 mt-4 grid grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="min-h-[96px] rounded-2xl border border-black/10 bg-white p-4">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="mt-2 h-7 w-24" />
                <Skeleton className="mt-1 h-3 w-32" />
              </div>
            ))}
          </div>
          {/* Critical inventory placeholder */}
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-40" />
            <SkeletonList rows={5} className="mt-2" />
          </section>
        </main>
      </div>
    )
  }

  const summaryToneClass = {
    success:  'bg-emerald-50 border-emerald-300 text-emerald-900',
    info:     'bg-amber-brand-50  border-amber-brand-200  text-amber-brand-900',
    warning:  'bg-amber-50   border-amber-300   text-amber-900',
    critical: 'bg-red-50     border-red-300     text-red-900',
  }[summary.tone] ?? 'bg-amber-brand-50 border-amber-brand-200 text-amber-brand-900'

  // Tone for the waste card based on target comparison.
  const wasteTone = todayWasteTotal > dailyTarget ? 'critical' : 'success'
  const wasteDelta = todayWasteTotal - yesterdayTotal

  // Lifetime savings tone — verified vs preliminary.
  const lifetimeVerified =
    Number(savingsSummary?.event_count ?? 0) > 0 &&
    Number(savingsSummary?.event_count) ===
      Number(savingsSummary?.confirmed_count)
  const lifetimeTotal = Number(savingsSummary?.lifetime_total ?? 0)

  // Today's date in human form for the header subtitle.
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'short', day: 'numeric',
  })

  return (
    <div className="min-h-full bg-white pb-12">
      <main className="mx-auto max-w-screen-sm">
        {/* Date subhead */}
        <div className="px-4 pt-4 text-sm text-black/55">{today}</div>

        {/* Welcome card — only shown when this restaurant hasn't
            finished the onboarding wizard yet. Manager can either
            launch the wizard or dismiss with the X (also marks
            onboarded so the card doesn't reappear). */}
        {showWelcome && (
          <section
            className="mx-4 mt-3 rounded-2xl border border-blue-300 bg-blue-50/80 p-4"
            aria-label="Welcome to your kitchen"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-medium text-blue-900/70">
                  Welcome
                </div>
                <div className="mt-1 text-base font-medium text-blue-900">
                  Set up your kitchen in a couple of minutes
                </div>
                <p className="mt-1 text-sm leading-snug text-blue-900/85">
                  We'll confirm your restaurant name + time zone, set your
                  daily targets, and point you at the import flow. You can
                  skip and configure later from Settings.
                </p>
              </div>
              <button
                type="button"
                onClick={handleDismissWelcome}
                aria-label="Dismiss welcome card"
                className="min-h-[36px] min-w-[36px] rounded-lg px-2 text-blue-900/55 hover:bg-blue-100 hover:text-blue-900"
              >
                ✕
              </button>
            </div>
            <button
              type="button"
              onClick={() => setWizardOpen(true)}
              className="mt-3 min-h-btn w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700"
            >
              Set up your kitchen →
            </button>
          </section>
        )}

        {/* AI morning brief — the single brief on this page. Was
            previously paired with a static "Today's brief" banner
            that read off lifetime-savings; that was redundant with
            the AI brief and customers asked which one to read first.
            One brief, one source of truth. */}
        <HuddleBriefCard
          loading={loading}
          settings={settings}
          todayWasteTotal={todayWasteTotal}
          yesterdayTotal={yesterdayTotal}
          todaySales={todaySales}
          todayLabor={todayLabor}
          todayReceiveTotal={todayReceiveTotal}
          todayReceiveCount={todayReceiveCount}
          todaySupplierCount={todaySupplierCount}
          itemsNeedingReorder={itemsNeedingReorder}
          criticalItems={criticalItems}
          restockCost={restockCost}
          topWastedToday={topWastedToday}
          todayAssignments={todayAssignments}
          activeStaff={activeStaff}
          wastePctOfSales={wastePctOfSales}
          laborPctOfSales={laborPctOfSales}
          receivingsPctOfSales={receivingsPctOfSales}
          primeCost7d={primeCost7d}
        />

        {/* Hours back since you joined — read-only render of the
            time-saved ledger (savings_events source='time_saved', logged
            by the approve-and-execute reorder executor). Dollars saved is
            the secondary line, sourced from the existing savings summary
            so it never disagrees with LifetimeSavings. */}
        <TimeSavedCard dollarsSaved={Number(savingsSummary?.lifetime_total ?? 0)} />

        {/* First-week setup checklist — only renders when the customer
            is in their 14-day onboarding window, has at least one
            unchecked milestone, and hasn't dismissed. Auto-hides
            once they graduate. */}
        <FirstWeekMilestonesCard onSwitchTab={onSwitchTab} />

        {/* Marketing automation nudge — shown once per restaurant per
            device, only after the menu is populated enough for the
            AI drafts to be useful. Dismissed permanently via "Not now."
            Hidden once the customer enables automation (the drafts
            banner below takes over). */}
        {shouldShowMarketingNudge({
          restaurantId: onboardingRestaurant?.id,
          itemCount,
          hasMarketingEnabled: !!marketing.settings?.enabled,
        }) && (
          <MarketingNudgeCard
            restaurantId={onboardingRestaurant?.id}
            onEnable={() => onSwitchTab?.('marketing')}
          />
        )}

        {/* Social drafts banner — only renders when the marketing
            automation cron has dropped fresh drafts that haven't been
            reviewed yet. Tapping switches to the Marketing tab so the
            manager can knock out the weekly review in 10 minutes. */}
        {draftCount > 0 && (
          <button
            type="button"
            onClick={() => onSwitchTab?.('marketing')}
            className="mx-4 mt-3 block w-[calc(100%-2rem)] rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-left transition hover:bg-blue-100/80 focus:outline-none focus:ring-2 focus:ring-blue-600/30"
            aria-label={`${AGENT_LABELS.marketingActivity} — ${draftCount} new draft${draftCount === 1 ? '' : 's'} ready to review`}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-medium text-blue-700">
                  {AGENT_LABELS.marketingActivity}
                </div>
                <div className="mt-0.5 text-sm font-medium text-blue-900">
                  {draftCount} new draft{draftCount === 1 ? '' : 's'} ready to review
                </div>
                <div className="text-xs text-blue-900/70">
                  Approve, edit, or reject — usually takes ~10 minutes.
                </div>
              </div>
              <span className="text-blue-700 text-lg" aria-hidden="true">→</span>
            </div>
          </button>
        )}

        {/* AI Receipts — deterministic recap of what each agent did this
            week. Scoped to the active unit; rows click through to the tab
            that owns the action. */}
        {currentRestaurantId && (
          <AiReceiptsCard
            scope={{ restaurantId: currentRestaurantId }}
            onTab={(tab) => onSwitchTab?.(tab)}
          />
        )}

        {/* Metric grid */}
        <section className="mx-4 mt-4 grid grid-cols-2 gap-3" aria-label="Key metrics">
          <MetricCard
            label="Today's waste"
            value={formatMoney(todayWasteTotal)}
            subtitle={
              wastePctOfSales != null
                ? `${wastePctOfSales.toFixed(1)}% of today's sales`
                : yesterdayTotal > 0
                  ? `${wasteDelta >= 0 ? '↑' : '↓'} ${formatMoney(Math.abs(wasteDelta))} vs yesterday`
                  : `Target ${formatMoney(dailyTarget)}`
            }
            tone={wasteTone}
            onClick={() => onSwitchTab?.('waste')}
          />
          <MetricCard
            label="Lifetime saved"
            value={formatMoney(lifetimeTotal)}
            subtitle={
              lifetimeTotal === 0
                ? 'Tracking begins after first day'
                : lifetimeVerified
                  ? '✓ Verified by owner'
                  : `${savingsSummary.event_count} event${savingsSummary.event_count === 1 ? '' : 's'} · pending review`
            }
            tone="success"
            onClick={() => onSwitchTab?.('waste')}
          />
          <MetricCard
            label="Reorder"
            value={`${itemsNeedingReorder.length} ${itemsNeedingReorder.length === 1 ? 'item' : 'items'}`}
            subtitle={
              itemsNeedingReorder.length === 0
                ? 'All stocked'
                : `${formatMoney(restockCost)} to restock`
            }
            tone={itemsNeedingReorder.length === 0 ? 'success' : 'warning'}
            onClick={() => onSwitchTab?.('reorder')}
          />
          <MetricCard
            label="Today's deliveries"
            value={formatMoney(todayReceiveTotal)}
            subtitle={
              todayReceiveCount === 0
                ? 'No deliveries logged'
                : receivingsPctOfSales != null
                  ? `${receivingsPctOfSales.toFixed(1)}% of today's sales`
                  : `${todayReceiveCount} ${todayReceiveCount === 1 ? 'delivery' : 'deliveries'} · ${todaySupplierCount} ${todaySupplierCount === 1 ? 'supplier' : 'suppliers'}`
            }
            tone="neutral"
            onClick={() => onSwitchTab?.('receive')}
          />
          <MetricCard
            label="Today's labor"
            value={formatMoney(todayLabor.cost)}
            subtitle={
              todayLabor.assignment_count === 0
                ? 'No staff scheduled'
                : laborPctOfSales != null
                  ? `${laborPctOfSales.toFixed(1)}% of today's sales`
                  : `${todayLabor.staff_count} staff · ${todayLabor.hours.toFixed(1)} hrs`
            }
            tone="neutral"
            onClick={() => onSwitchTab?.('schedule')}
          />
          <MetricCard
            label="Today's sales"
            value={
              todaySales
                ? formatMoney(Number(todaySales.net_sales ?? todaySales.gross_sales ?? 0))
                : '—'
            }
            subtitle={
              todaySales
                ? (todaySales.transaction_count
                    ? `${todaySales.transaction_count} ${todaySales.transaction_count === 1 ? 'order' : 'orders'}`
                    : 'imported from POS')
                : 'No sales imported'
            }
            tone="neutral"
          />
        </section>

        {/* Prime Cost — 7-day rolling food + labor cost synthesis.
            The single most-watched restaurant cost-control metric.
            Only renders if sales data exists in the window — without
            sales, there's nothing to compute against. */}
        {primeCost7d?.prime_pct != null && primeCost7d.days_with_sales > 0 && (
          <PrimeCostCard data={primeCost7d} />
        )}

        {/* Critical inventory */}
        <section className="mx-4 mt-6" aria-label="Critical inventory">
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="text-base font-medium">Critical inventory</h2>
            {itemsNeedingReorder.length > 0 && (
              <button
                type="button"
                onClick={() => onSwitchTab?.('reorder')}
                className="rounded px-1 text-xs font-medium text-blue-600 hover:underline"
              >
                View all {itemsNeedingReorder.length} →
              </button>
            )}
          </div>
          {criticalItems.length === 0 ? (
            <div className="rounded-xl border border-dashed border-emerald-200 bg-emerald-50/40 px-4 py-6 text-center text-sm text-emerald-900/80">
              All items at or above par. Nothing critical right now.
            </div>
          ) : (
            <ul className="overflow-hidden rounded-xl border border-black/10">
              {criticalItems.map((i) => {
                const tone =
                  i.pctRemaining < 25
                    ? 'bg-red-600'
                    : i.pctRemaining < 50
                      ? 'bg-amber-brand-400'
                      : 'bg-amber-brand-200'
                return (
                  <li
                    key={i.id}
                    className="border-b border-black/5 px-4 py-3 last:border-b-0"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium truncate">{i.name}</span>
                      <span className="text-sm tabular-nums text-black/70">
                        {formatQty(i.current_on_hand, i.unit)} of{' '}
                        {formatQty(i.par_qty, i.unit)} ·{' '}
                        <span className="font-medium">
                          {i.pctRemaining.toFixed(0)}%
                        </span>
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-black/5">
                      <div
                        className={`h-full ${tone}`}
                        style={{
                          width: `${Math.max(2, Math.min(100, i.pctRemaining))}%`,
                        }}
                        aria-hidden="true"
                      />
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        {/* Today's Roster — staff scheduled for today */}
        <section className="mx-4 mt-6" aria-label="Today's roster">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h2 className="text-base font-medium">
              Today's roster
              <span className="ml-2 text-xs font-normal text-black/55">
                {todayAssignments.length === 0
                  ? 'no shifts yet'
                  : `${todayAssignments.length} ${todayAssignments.length === 1 ? 'shift' : 'shifts'}`}
              </span>
            </h2>
            <button
              type="button"
              onClick={() => setRosterModalOpen(true)}
              disabled={activeStaff.length === 0}
              title={activeStaff.length === 0 ? 'Add staff in Settings first' : undefined}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              + Add
            </button>
          </div>

          {todayAssignments.length === 0 ? (
            <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
              {activeStaff.length === 0 ? (
                <>
                  Add staff in <span className="font-medium text-blue-600">Settings → Staff</span> first,
                  then come back here to mark who's working today.
                </>
              ) : (
                <>
                  No one scheduled yet today. Tap{' '}
                  <span className="font-medium text-blue-600">+ Add</span>{' '}
                  to add the first shift.
                </>
              )}
            </div>
          ) : (
            <ul className="overflow-hidden rounded-xl border border-black/10">
              {todayAssignments.map((a) => {
                const isRemoving = removingAssignmentId === a.id
                const start = (a.start_time || '').slice(0, 5)
                const end   = (a.end_time   || '').slice(0, 5)
                const wage  = Number(a.staff?.hourly_wage || 0)
                const hours = a.start_time && a.end_time
                  ? (() => {
                      const [sh, sm] = a.start_time.split(':').map(Number)
                      const [eh, em] = a.end_time.split(':').map(Number)
                      let endMin = eh * 60 + em
                      const startMin = sh * 60 + sm
                      if (endMin < startMin) endMin += 24 * 60
                      return Math.max(0, (endMin - startMin) / 60)
                    })()
                  : 0
                const cost = hours * wage
                const statusBadge =
                  (a.status && a.status !== 'scheduled') ? (
                    <span className="ml-2">
                      <StatusPill tone={toneFor('shift', a.status)}>
                        {labelFor('shift', a.status)}
                      </StatusPill>
                    </span>
                  ) : null
                return (
                  <li
                    key={a.id}
                    className={`flex items-center justify-between gap-3 border-b border-black/5 px-4 py-3 last:border-b-0 ${isRemoving ? 'opacity-50' : ''}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-medium">
                        {a.staff?.name ?? '(deleted staff)'}
                        {statusBadge}
                      </div>
                      <div className="truncate text-xs text-black/60">
                        {start && end ? `${start}–${end}` : 'time TBD'}
                        {' · '}
                        {hours > 0 ? `${hours.toFixed(1)} hrs` : '0 hrs'}
                        {a.role ? ` · ${a.role}` : (a.staff?.role ? ` · ${a.staff.role}` : '')}
                      </div>
                    </div>
                    <div className="text-right text-sm font-medium tabular-nums">
                      {formatMoney(cost)}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveAssignment(a)}
                      disabled={isRemoving}
                      aria-label={`Remove ${a.staff?.name ?? 'assignment'}`}
                      title="Remove from today's roster"
                      className="min-h-btn min-w-[44px] rounded-lg px-2 text-base text-black/40 hover:bg-red-50 hover:text-red-600 active:bg-red-100 disabled:cursor-wait disabled:opacity-50"
                    >
                      {isRemoving ? '…' : '✕'}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        {/* Quick actions footer */}
        <section className="mx-4 mt-6 mb-2" aria-label="Quick actions">
          <h2 className="mb-2 text-base font-medium">Quick actions</h2>
          <div className="grid grid-cols-3 gap-2">
            <QuickAction
              label="Mark waste"
              onClick={() => onSwitchTab?.('waste')}
            />
            <QuickAction
              label="Log delivery"
              onClick={() => onSwitchTab?.('receive')}
            />
            <QuickAction
              label="Send orders"
              onClick={() => onSwitchTab?.('reorder')}
            />
          </div>
        </section>

        {/* AI ask bar — sticky bottom of the Huddle screen. Phase 1
            stub opens a "coming soon" modal; the affordance teaches
            customers that AI is the way to interact with the app. */}
        <AskAiBar placeholder="Ask AI anything…" />
      </main>

      <RosterAssignmentModal
        open={rosterModalOpen}
        onClose={() => setRosterModalOpen(false)}
        onCreate={handleAddToRoster}
        activeStaff={activeStaff}
        shiftDate={todayDateStr}
      />

      <OnboardingWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        restaurant={onboardingRestaurant}
        onUpdateBasics={updateRestaurantBasics}
        onMarkOnboarded={markOnboarded}
      />

      <Toast message={toast} onDone={() => setToast('')} />
    </div>
  )
}

// ---------- helpers ----------------------------------------

function MetricCard({ label, value, subtitle, tone = 'neutral', onClick }) {
  const toneClass = {
    neutral:  'bg-white       border-black/10',
    success:  'bg-emerald-50  border-emerald-200',
    warning:  'bg-amber-50    border-amber-200',
    critical: 'bg-red-50      border-red-200',
  }[tone]
  const valueColor = {
    neutral:  'text-black',
    success:  'text-emerald-700',
    warning:  'text-amber-800',
    critical: 'text-red-700',
  }[tone]

  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-[96px] rounded-2xl border ${toneClass} p-4 text-left transition hover:shadow-sm active:scale-[.98] focus:outline-none focus:ring-2 focus:ring-blue-600/30`}
    >
      <div className="text-[11px] font-medium text-black/55">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-medium tabular-nums ${valueColor}`}>
        {value}
      </div>
      {subtitle && (
        <div className="mt-0.5 text-xs leading-snug text-black/60">
          {subtitle}
        </div>
      )}
    </button>
  )
}

// Prime cost synthesis card — 7-day rolling food + labor as % of sales.
// Industry healthy: 55-60%. Color-codes by zone so a manager can read
// the health at a glance without parsing the number.
function PrimeCostCard({ data }) {
  const { cogs_pct, labor_pct, prime_pct, days_with_sales, sales, receivings, labor } = data
  // Health zones based on prime cost. Tuned to match industry norms.
  const zone =
    prime_pct < 55 ? 'excellent' :
    prime_pct < 60 ? 'healthy' :
    prime_pct < 65 ? 'warning' :
                     'critical'
  const zoneStyle = {
    excellent: { bg: 'bg-emerald-50',  border: 'border-emerald-300',  text: 'text-emerald-900',  label: 'Excellent' },
    healthy:   { bg: 'bg-emerald-50/60', border: 'border-emerald-200', text: 'text-emerald-900', label: 'On target' },
    warning:   { bg: 'bg-amber-50',    border: 'border-amber-300',    text: 'text-amber-900',    label: 'Watch this' },
    critical:  { bg: 'bg-red-50',      border: 'border-red-300',      text: 'text-red-900',      label: 'Over target' },
  }[zone]
  return (
    <section
      className={`mx-4 mt-4 rounded-2xl border ${zoneStyle.border} ${zoneStyle.bg} p-4`}
      aria-label="Prime cost (7-day rolling)"
    >
      <div className="flex items-baseline justify-between">
        <div className="text-[11px] font-medium opacity-70">
          Prime cost · last 7 days
        </div>
        <div className={`text-[11px] font-medium ${zoneStyle.text}`}>
          {zoneStyle.label}
        </div>
      </div>
      <div className={`mt-1 flex items-baseline gap-3 ${zoneStyle.text}`}>
        <div className="text-3xl font-medium tabular-nums">
          {prime_pct.toFixed(1)}%
        </div>
        <div className="text-xs opacity-75">
          of sales · industry healthy 55–60%
        </div>
      </div>

      {/* Breakdown: food cost % and labor % side by side */}
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg bg-white/60 px-3 py-2">
          <div className="text-[10px] font-medium text-black/55">
            Food cost
          </div>
          <div className="mt-0.5 text-lg font-medium tabular-nums">
            {cogs_pct.toFixed(1)}%
          </div>
          <div className="text-[10px] text-black/55">
            target 28–32%
          </div>
        </div>
        <div className="rounded-lg bg-white/60 px-3 py-2">
          <div className="text-[10px] font-medium text-black/55">
            Labor
          </div>
          <div className="mt-0.5 text-lg font-medium tabular-nums">
            {labor_pct.toFixed(1)}%
          </div>
          <div className="text-[10px] text-black/55">
            target 25–30%
          </div>
        </div>
      </div>

      <div className="mt-2 text-[11px] text-black/55">
        Based on {days_with_sales} {days_with_sales === 1 ? 'day' : 'days'} with sales:{' '}
        {formatMoney(receivings)} food + {formatMoney(labor)} labor against{' '}
        {formatMoney(sales)} sales.
      </div>
    </section>
  )
}

function QuickAction({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-h-btn rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 text-sm font-medium text-blue-700 transition hover:bg-blue-100 active:bg-blue-200"
    >
      {label}
    </button>
  )
}

// Heuristic summary sentence. Order matters — first matching branch
// wins. The summary should always feel like the "most useful one
// thing to know right now" rather than a recap of all metrics.
function generateSummary({
  todayWasteTotal,
  yesterdayTotal,
  dailyTarget,
  criticalItems,
  itemsNeedingReorder,
  savingsSummary,
  topWastedToday,
}) {
  // 1. Over today's waste target — most urgent
  if (todayWasteTotal > dailyTarget) {
    const over = todayWasteTotal - dailyTarget
    return {
      tone: 'critical',
      text: `Waste hit ${formatMoney(todayWasteTotal)} today — ${formatMoney(over)} over your ${formatMoney(dailyTarget)} target.${
        topWastedToday ? ` ${topWastedToday.name} was the biggest driver.` : ''
      }`,
    }
  }

  // 2. Critical inventory — multiple items under 25%
  const reallyLow = criticalItems.filter((i) => i.pctRemaining < 25).length
  if (reallyLow >= 2) {
    return {
      tone: 'warning',
      text: `${reallyLow} items are critically low. Check the inventory list below or jump to Reorder.`,
    }
  }

  // 3. Single item critically low
  if (reallyLow === 1) {
    const i = criticalItems.find((x) => x.pctRemaining < 25)
    return {
      tone: 'warning',
      text: `${i.name} is at ${i.pctRemaining.toFixed(0)}% of par — order today before service.`,
    }
  }

  // 4. Significant day-over-day spike
  if (yesterdayTotal > 0 && todayWasteTotal > yesterdayTotal * 1.5) {
    const upPct = ((todayWasteTotal - yesterdayTotal) / yesterdayTotal * 100).toFixed(0)
    return {
      tone: 'warning',
      text: `Waste up ${upPct}% from yesterday.${
        topWastedToday ? ` ${topWastedToday.name} drove most of it.` : ' Review the Top 3.'
      }`,
    }
  }

  // 5. Lots of items below par — ordering day prompt
  if (itemsNeedingReorder.length >= 10) {
    return {
      tone: 'info',
      text: `${itemsNeedingReorder.length} items below par across the kitchen. Today's a good ordering day.`,
    }
  }

  // 6. Healthy day — well under target
  if (dailyTarget > 0 && todayWasteTotal < dailyTarget * 0.5) {
    const saved = dailyTarget - todayWasteTotal
    return {
      tone: 'success',
      text: `Tight day — ${formatMoney(saved)} under your ${formatMoney(dailyTarget)} daily target so far.`,
    }
  }

  // 7. Lifetime savings shoutout
  const lifetime = Number(savingsSummary?.lifetime_total ?? 0)
  if (lifetime > 100) {
    return {
      tone: 'success',
      text: `AI Restaurant Manager has saved you ${formatMoney(lifetime)} so far. Keep it tight.`,
    }
  }

  // 8. Default
  return {
    tone: 'info',
    text: 'Quiet day. All systems looking healthy.',
  }
}
